import React from 'react';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useThemeMode } from './OutreachThemeProvider';
import StaffAvatar from './StaffAvatar';

const AdminTopBar = ({
  title,
  subtitle,
  user,
  profile,
  greeting = '',
  statusLabel = '',
  onMenuClick,
  onProfileClick,
  onLogout,
  profileHomeAriaLabel = 'Go to My Dashboard',
  toolbarMinHeight = 64,
}) => {
  const { mode, toggleThemeMode } = useThemeMode();
  const nextModeLabel = mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  const displayName = profile?.full_name || profile?.email || 'Staff user';

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: (theme) => theme.custom.headerGradient,
        backdropFilter: 'blur(4px)',
        boxShadow: (theme) => theme.custom.softShadow,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 2, minHeight: `${toolbarMinHeight}px` }}>
        <IconButton
          color="inherit"
          edge="start"
          aria-label="Open navigation menu"
          onClick={onMenuClick}
          sx={{ display: { lg: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
          {/*
            Shell chrome, not a document section: this identifies the current
            route inside the persistent shell, so it keeps the `h6` visual scale
            but renders as a plain element. The routed page owns the `<h1>`.
          */}
          <Typography variant="h6" component="div" noWrap>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {/* Spacer stays mounted at every width so hiding the greeting on narrow
            screens cannot collapse the toolbar layout. */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          {greeting ? (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                px: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {greeting}
              </Typography>
              {statusLabel ? (
                <>
                  <Typography variant="body2" color="text.secondary" aria-hidden="true">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {statusLabel}
                  </Typography>
                </>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {profile ? (
            <ButtonBase
              aria-label={profileHomeAriaLabel}
              onClick={onProfileClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: { xs: 0.5, sm: 1.5 },
                py: 0.75,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.custom.topBarButton,
                '&:hover': {
                  backgroundColor: (theme) => theme.custom.drawerHover,
                },
              }}
            >
              <StaffAvatar
                avatarUrl={profile.avatar_url}
                fullName={displayName}
                size={36}
              />
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  minWidth: 0,
                  textAlign: 'left',
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.2, fontWeight: 700 }} noWrap>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {profile.role}
                </Typography>
              </Box>
            </ButtonBase>
          ) : null}

          <Tooltip title={nextModeLabel}>
            <IconButton
              color="inherit"
              aria-label={nextModeLabel}
              onClick={toggleThemeMode}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.custom.topBarButton,
                '&:hover': {
                  backgroundColor: (theme) => theme.custom.drawerHover,
                },
              }}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {user ? (
            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              aria-label="Log out"
              onClick={onLogout}
              sx={{
                whiteSpace: 'nowrap',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.custom.topBarButton,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                },
              }}
            >
              Logout
            </Button>
          ) : null}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminTopBar;
